import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';

const AdminResourcesPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const categoryIcons = {
    audio: '🎵',
    podcast: '🎧',
    activity: '📝',
    digital: '📚',
    other: '📄'
  };

  const categoryLabels = {
    audio: 'Audio',
    podcast: 'Podcast',
    activity: 'Activity',
    digital: 'Digital Product',
    other: 'Resource'
  };

  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedResource, setSelectedResource] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');

  const fetchResources = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.getProducts();
      const raw = res.products || res.data || [];
      const mapped = raw.map(p => {
        const creator = p.author || p.createdById || p.physiotherapist || {};
        return {
          id: p._id || p.id,
          title: p.title || 'Untitled Resource',
          therapistName: creator.name || p.authorName || 'Therapist',
          category: p.type || p.category || 'digital',
          fileSize: p.fileSize || 5.0,
          uploadDate: p.createdAt ? new Date(p.createdAt).toISOString().split('T')[0] : 'N/A',
          status: p.status || (p.isApproved ? 'approved' : 'pending'),
          views: p.views || p.downloads || 0,
          rejectionReason: p.rejectionReason || ''
        };
      });
      setResources(mapped);
    } catch (err) {
      console.error('Failed to fetch resources:', err);
      setError(err.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchResources();
  }, [authSession?.token, navigate]);

  // Calculate statistics
  const stats = {
    total: resources.length,
    approved: resources.filter(r => r.status === 'approved').length,
    pending: resources.filter(r => r.status === 'pending').length,
    rejected: resources.filter(r => r.status === 'rejected').length,
    totalViews: resources.reduce((sum, r) => sum + r.views, 0),
    totalSize: (resources.reduce((sum, r) => sum + r.fileSize, 0)).toFixed(2)
  };

  // Filter resources
  const filtered = resources.filter(r => {
    const matchesSearch = r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.therapistName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || r.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleApprove = async (id) => {
    try {
      await adminApi.reviewProduct(id, { decision: 'approved', status: 'approved' });
      setResources(resources.map(r => r.id === id ? { ...r, status: 'approved' } : r));
      setSelectedResource(null);
    } catch (err) {
      alert(err.message || 'Failed to approve resource');
    }
  };

  const handleReject = async (id) => {
    if (!rejectionNote.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    try {
      await adminApi.reviewProduct(id, { decision: 'rejected', status: 'rejected', note: rejectionNote });
      setResources(resources.map(r => r.id === id ? { ...r, status: 'rejected', rejectionReason: rejectionNote } : r));
      setRejectionNote('');
      setSelectedResource(null);
    } catch (err) {
      alert(err.message || 'Failed to reject resource');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await adminApi.deleteProduct(id);
        setResources(resources.filter(r => r.id !== id));
        setSelectedResource(null);
      } catch (err) {
        alert(err.message || 'Failed to delete resource');
      }
    }
  };

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Link to="/admin-dashboard" style={{ color: '#667eea', textDecoration: 'none', marginBottom: '20px', display: 'inline-block', fontWeight: 500 }}>
            ← Back to Dashboard
          </Link>

          <div style={{ marginBottom: '30px' }}>
            <h1 style={{ fontSize: '32px', color: '#333', margin: '20px 0 10px 0', fontWeight: 700 }}>📦 Manage Learning Resources</h1>
            <p style={{ color: '#666', marginBottom: '0' }}>Review, approve, and manage all therapist-uploaded resources</p>
          </div>

          {/* Statistics Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '15px',
            marginBottom: '30px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #667eea'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>TOTAL RESOURCES</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#333', fontWeight: 700 }}>{stats.total}</h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #10b981'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>✓ APPROVED</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#10b981', fontWeight: 700 }}>{stats.approved}</h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #f59e0b'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>⏳ PENDING</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#f59e0b', fontWeight: 700 }}>{stats.pending}</h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #ef4444'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>✗ REJECTED</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#ef4444', fontWeight: 700 }}>{stats.rejected}</h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #8b5cf6'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>👁️ TOTAL VIEWS</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#8b5cf6', fontWeight: 700 }}>{stats.totalViews}</h2>
            </div>
          </div>

          {/* Search and Filters */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Search by Title or Therapist
                </label>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Filter by Category
                </label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all">All Categories</option>
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <option key={key} value={key}>{categoryIcons[key]} {label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Filter by Status
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="pending">⏳ Pending</option>
                  <option value="approved">✓ Approved</option>
                  <option value="rejected">✗ Rejected</option>
                </select>
              </div>
            </div>
          </div>

          {/* Resources Table */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                <p style={{ fontSize: '16px', margin: 0 }}>No resources found matching your filters</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Resource</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: 600, color: '#666' }}>Therapist</th>
                      <th style={{ padding: '15px', textAlign: 'center', fontWeight: 600, color: '#666' }}>Category</th>
                      <th style={{ padding: '15px', textAlign: 'center', fontWeight: 600, color: '#666' }}>Size</th>
                      <th style={{ padding: '15px', textAlign: 'center', fontWeight: 600, color: '#666' }}>Views</th>
                      <th style={{ padding: '15px', textAlign: 'center', fontWeight: 600, color: '#666' }}>Status</th>
                      <th style={{ padding: '15px', textAlign: 'center', fontWeight: 600, color: '#666' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((resource, index) => (
                      <tr
                        key={resource.id}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: index % 2 === 0 ? 'white' : '#f9fafb'
                        }}
                      >
                        <td style={{ padding: '15px' }}>
                          <div style={{ fontWeight: 500, color: '#333' }}>
                            {categoryIcons[resource.category]} {resource.title}
                          </div>
                          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
                            {new Date(resource.uploadDate).toLocaleDateString()}
                          </p>
                        </td>
                        <td style={{ padding: '15px', color: '#555' }}>{resource.therapistName}</td>
                        <td style={{ padding: '15px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            background: '#e5e7eb',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#666'
                          }}>
                            {categoryLabels[resource.category]}
                          </span>
                        </td>
                        <td style={{ padding: '15px', textAlign: 'center', color: '#555' }}>
                          {resource.fileSize}MB
                        </td>
                        <td style={{ padding: '15px', textAlign: 'center', color: '#555', fontWeight: 500 }}>
                          {resource.views}
                        </td>
                        <td style={{ padding: '15px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: resource.status === 'approved' ? '#10b981' : resource.status === 'pending' ? '#f59e0b' : '#ef4444',
                            background: resource.status === 'approved' ? '#d1fae5' : resource.status === 'pending' ? '#fef3c7' : '#fee2e2'
                          }}>
                            {resource.status === 'approved' ? '✓ Approved' : resource.status === 'pending' ? '⏳ Pending' : '✗ Rejected'}
                          </span>
                        </td>
                        <td style={{ padding: '15px', textAlign: 'center' }}>
                          <button
                            onClick={() => setSelectedResource(resource)}
                            style={{
                              padding: '6px 12px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#5568d3'}
                            onMouseOut={(e) => e.currentTarget.style.background = '#667eea'}
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resource Management Modal */}
      {selectedResource && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px rgba(0,0,0,0.15)'
          }}>
            <h2 style={{ fontSize: '20px', margin: '0 0 15px 0', color: '#333', fontWeight: 700 }}>
              {categoryIcons[selectedResource.category]} {selectedResource.title}
            </h2>

            <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Therapist:</strong> {selectedResource.therapistName}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Category:</strong> {categoryLabels[selectedResource.category]}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>File Size:</strong> {selectedResource.fileSize}MB
              </p>
              <p style={{ margin: 0 }}>
                <strong>Uploaded:</strong> {new Date(selectedResource.uploadDate).toLocaleDateString()}
              </p>
            </div>

            {selectedResource.status === 'rejected' && selectedResource.rejectionReason && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#991b1b'
              }}>
                <strong>Rejection Reason:</strong> {selectedResource.rejectionReason}
              </div>
            )}

            {selectedResource.status === 'pending' && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333', fontSize: '14px' }}>
                    Rejection Reason (if applicable)
                  </label>
                  <textarea
                    value={rejectionNote}
                    onChange={(e) => setRejectionNote(e.target.value)}
                    placeholder="Provide a reason for rejection..."
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      minHeight: '80px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleApprove(selectedResource.id)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ✓ Approve
                  </button>
                  <button
                    onClick={() => handleReject(selectedResource.id)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ✗ Reject
                  </button>
                </div>
              </>
            )}

            {selectedResource.status === 'approved' && (
              <div style={{
                background: '#d1fae5',
                border: '1px solid #6ee7b7',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#065f46'
              }}>
                ✓ This resource is approved and visible to clients
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleDelete(selectedResource.id)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#e5e7eb',
                  color: '#ef4444',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🗑️ Delete
              </button>
              <button
                onClick={() => {
                  setSelectedResource(null);
                  setRejectionNote('');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#f3f4f6',
                  color: '#333',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminResourcesPage;
